import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Printer, ScanBarcode, Search } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import {
  apiError, fmtDate, fmtDateTime, printVolumeLabels,
  type MaterialRow, type VolumeSearchRow, type VolumeStatus,
} from '../lib/industria'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

const VOLUME_BADGE: Record<VolumeStatus, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  fechado: 'info', aberto: 'warning', consumido: 'default', quarentena: 'danger',
}

export function IndustriaEtiquetasPage() {
  const [search, setSearch] = useState('')
  const [materialId, setMaterialId] = useState('')
  const [status, setStatus] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [scan, setScan] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [printing, setPrinting] = useState(false)

  const { data: materials = [] } = useQuery<MaterialRow[]>({
    queryKey: ['industria', 'materials', 'all'],
    queryFn: async () => (await api.get('/materials')).data,
  })

  const { data: volumes = [], isLoading } = useQuery<VolumeSearchRow[]>({
    queryKey: ['industria', 'volumes', search, materialId, status],
    queryFn: async () => (await api.get('/material-volumes', {
      params: {
        search: search || undefined,
        materialId: materialId || undefined,
        status: status || undefined,
        limit: 300,
      },
    })).data,
  })

  function toggle(id: string) {
    setSelected(s => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }

  function toggleAll() {
    setSelected(s => (s.size === volumes.length ? new Set() : new Set(volumes.map(v => v.id))))
  }

  // Bipar o código põe o volume na seleção sem depender do filtro da lista.
  async function handleScan(code: string) {
    setError(null)
    try {
      const { data } = await api.get(`/material-volumes/by-code/${encodeURIComponent(code.trim())}`)
      setSelected(s => new Set(s).add(data.id))
      setScan('')
    } catch (e) {
      setError(apiError(e, `Volume ${code} não encontrado`))
    }
  }

  async function handlePrint(format: 'label' | 'a4') {
    setPrinting(true)
    setError(null)
    try {
      await printVolumeLabels([...selected], format)
    } catch (e) {
      setError(apiError(e, 'Não foi possível gerar as etiquetas'))
    } finally {
      setPrinting(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Etiquetas de volume</h2>
          <p className="text-sm text-gray-500">
            Reimpressão da etiqueta que o operador bipa — código de barras, lote, dono e quantidade
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            disabled={selected.size === 0}
            loading={printing}
            onClick={() => handlePrint('a4')}
          >
            <Printer className="h-4 w-4" /> A4 (4 por folha)
          </Button>
          <Button
            disabled={selected.size === 0}
            loading={printing}
            onClick={() => handlePrint('label')}
          >
            <Printer className="h-4 w-4" /> Imprimir {selected.size > 0 ? `(${selected.size})` : ''}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">Buscar</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Volume, material ou lote"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="w-64">
            <label className="mb-1 block text-sm font-medium text-gray-700">Material</label>
            <select className={selectClass} value={materialId} onChange={e => setMaterialId(e.target.value)}>
              <option value="">Todos</option>
              {materials.map(m => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
            </select>
          </div>
          <div className="w-40">
            <label className="mb-1 block text-sm font-medium text-gray-700">Situação</label>
            <select className={selectClass} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">Todas</option>
              <option value="fechado">Fechado</option>
              <option value="aberto">Aberto</option>
              <option value="consumido">Consumido</option>
              <option value="quarentena">Quarentena</option>
            </select>
          </div>
          <div className="w-56">
            <label className="mb-1 block text-sm font-medium text-gray-700">Bipar volume</label>
            <div className="relative">
              <ScanBarcode className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="pl-9 font-mono"
                placeholder="WS-M-000123"
                value={scan}
                onChange={e => setScan(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && scan.trim()) handleScan(scan) }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">
            {volumes.length} volume(s){selected.size > 0 ? ` · ${selected.size} selecionado(s)` : ''}
          </span>
          {volumes.length > 0 && (
            <button className="text-sm text-blue-600 hover:underline" onClick={toggleAll}>
              {selected.size === volumes.length ? 'Limpar seleção' : 'Selecionar todos'}
            </button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : volumes.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">
              Nenhum volume — eles nascem no recebimento
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-10 px-4 py-2.5"></th>
                    <th className="px-4 py-2.5 text-left font-medium">Volume</th>
                    <th className="px-4 py-2.5 text-left font-medium">Material</th>
                    <th className="px-4 py-2.5 text-left font-medium">Lote</th>
                    <th className="px-4 py-2.5 text-left font-medium">Dono</th>
                    <th className="px-4 py-2.5 text-left font-medium">Local</th>
                    <th className="px-4 py-2.5 text-right font-medium">Atual / inicial</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                    <th className="px-4 py-2.5 text-left font-medium">Criado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {volumes.map(v => (
                    <tr
                      key={v.id}
                      className={selected.has(v.id) ? 'bg-blue-50/50' : 'hover:bg-gray-50/60'}
                    >
                      <td className="px-4 py-2.5">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={selected.has(v.id)}
                          onChange={() => toggle(v.id)}
                        />
                      </td>
                      <td className="px-4 py-2.5 font-mono font-medium text-gray-900">{v.code}</td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/industria/materiais/${v.materialId}`}
                          className="text-gray-700 hover:text-blue-600 hover:underline"
                        >
                          {v.materialName}
                        </Link>
                        <span className="block font-mono text-xs text-gray-400">{v.materialCode}</span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-gray-600">
                        {v.lotNumber ?? '—'}
                        {v.expiryDate && (
                          <span className="block text-xs text-gray-400">val. {fmtDate(v.expiryDate)}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{v.ownerName ?? 'WS (próprio)'}</td>
                      <td className="px-4 py-2.5 font-mono text-gray-600">{v.locationCode ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {v.qtyCurrent.toLocaleString('pt-BR')}
                        <span className="text-gray-400"> / {v.qtyInitial.toLocaleString('pt-BR')}</span>
                        <span className="ml-1 text-xs text-gray-400">{v.unit}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant={VOLUME_BADGE[v.status]}>{v.status}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(v.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
